import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';

import { PARTICIPATION, isValidEmail } from '../invitationInternalConstants';

// Confirm popup for Invitation - Internal (DOD §Button Confirm).
//
//  - Option box: Present / Delegate.
//  - When Delegate is chosen, a Participant Information card appears with Name
//    (text) and Email (text, email-format validated).
//  - Submit -> onConfirm({ mode, name, email }); the parent flips status to
//    Confirmed and stores the participant.
//  - Cancel -> onClose; nothing is saved.
const ConfirmParticipationModal = ({
  show,
  submitting,
  onConfirm,
  onClose,
}) => {
  const [mode, setMode] = useState(PARTICIPATION.PRESENT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset the form every time the popup opens so a previous attempt never leaks.
  useEffect(() => {
    if (show) {
      setMode(PARTICIPATION.PRESENT);
      setName('');
      setEmail('');
      setTouched(false);
    }
  }, [show]);

  const isDelegate = mode === PARTICIPATION.DELEGATE;
  const nameInvalid = isDelegate && !name.trim();
  const emailInvalid = isDelegate && !isValidEmail(email);

  const handleSubmit = () => {
    if (isDelegate && (nameInvalid || emailInvalid)) {
      setTouched(true);
      return;
    }
    onConfirm({ mode, name: name.trim(), email: email.trim() });
  };

  return (
    <Modal isOpen={show} toggle={onClose} centered data-bs-backdrop='static'>
      <ModalHeader toggle={onClose}>Confirm Attendance</ModalHeader>
      <ModalBody>
        <p className='text-muted fs-13 mb-3'>
          Confirm your attendance for this tender-opening session.
        </p>

        {/* Option box: Present / Delegate */}
        <div className='d-flex gap-2 mb-3'>
          {[PARTICIPATION.PRESENT, PARTICIPATION.DELEGATE].map(opt => (
            <div
              key={opt}
              role='button'
              className={`flex-grow-1 border rounded p-3 text-center ${
                mode === opt ? 'border-primary bg-soft-primary' : ''
              }`}
              onClick={() => setMode(opt)}
            >
              <div className='form-check d-flex justify-content-center mb-1'>
                <Input
                  type='radio'
                  name='participation-mode'
                  className='form-check-input'
                  checked={mode === opt}
                  onChange={() => setMode(opt)}
                />
              </div>
              <span className='fw-semibold'>{opt}</span>
            </div>
          ))}
        </div>

        {/* Participant information — Delegate only */}
        {isDelegate && (
          <Card className='border shadow-none mb-0'>
            <CardBody>
              <h6 className='fw-semibold mb-3'>Participant Information</h6>
              <div className='mb-3'>
                <Label className='form-label'>
                  Name <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='text'
                  value={name}
                  invalid={touched && nameInvalid}
                  onChange={e => setName(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
                <FormFeedback>Name is required</FormFeedback>
              </div>
              <div className='mb-1'>
                <Label className='form-label'>
                  Email <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='text'
                  value={email}
                  invalid={touched && emailInvalid}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
                <FormFeedback>Enter a valid email address</FormFeedback>
              </div>
            </CardBody>
          </Card>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color='light' onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color='primary' onClick={handleSubmit} disabled={submitting}>
          <i className='ri-check-line align-bottom me-1'></i>
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmParticipationModal;
